import {
  useState,
  useEffect,
  useRef,
  MouseEventHandler,
  useCallback,
} from 'react';

import { Tooltip } from '@nike/eds';
import { Shoe, CreateRuleFilled } from '@nike/nike-design-system-icons';
import classnames from 'classnames';
import dayjs from 'dayjs';

import { ProductItem } from 'clientAPI/nickname';
import { useLoaderData, useRevalidator } from 'react-router-dom';

import Input from 'components/Input';
import NBYIcon from 'components/NBYIcon';

import styles from './NicknameProductItem.module.css';
import { isExistScope } from '../utils/authHelpers';
import { CHANNEL_NAMES } from '../constants';

type NicknameProductItemProps = {
  data: ProductItem;
  onUpdateNickname: (
    nicknames: string[],
    isDelete: boolean,
    nickName: string,
  ) => void;
};

export default function NicknameProductItem(props: NicknameProductItemProps) {
  const { data: product, onUpdateNickname } = props;
  const addBlockRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isAddBlockShow, setIsAddBlockShow] = useState(false);
  const [isActivationsExpanded, setIsActivationsExpanded] = useState(false);
  const loaderData = useLoaderData() as { access_token: string };
  const jwt = loaderData.access_token;
  const canWrite = isExistScope('search360:nickname:write', jwt);
  const revalidator = useRevalidator();
  const convertResource: Record<string, string[]> = {};

  if (!product.source) {
    product.source = {};
  }

  if (Object.entries(product.source).length == 0 && product.nickName) {
    for (const nick_name of product.nickName) {
      product.source[nick_name] = '360UI';
    }
  }

  for (const [key, value] of Object.entries(product.source)) {
    if (convertResource[value]) {
      convertResource[value].push(key);
    } else {
      convertResource[value] = [key];
    }
  }

  useEffect(() => {
    return () => {
      document.removeEventListener('click', onGlobalCloseClick);
    };
  }, []);

  const openAddBlock: MouseEventHandler<HTMLDivElement> = (e) => {
    if (!isAddBlockShow) {
      setIsAddBlockShow(true);
      inputRef.current?.focus();
      setTimeout(
        () => document.addEventListener('click', onGlobalCloseClick),
        100,
      );
    }
  };

  const onGlobalCloseClick = useCallback((e: MouseEvent) => {
    if (
      e.target instanceof Node &&
      addBlockRef.current &&
      !addBlockRef.current.contains(e.target)
    ) {
      setIsAddBlockShow(false);
      document.removeEventListener('click', onGlobalCloseClick);
    }
  }, []);

  const addNewNickname = (value: string) => {
    // Split by comma, semicolon and trim each value
    const newNicknames = value
      .split(/[,;]+/)
      .map((v) => v.trim())
      .filter((v) => v.length > 0)
      .filter((v) => !product.nickName.includes(v)); // Remove duplicates

    if (newNicknames.length === 0) {
      setIsAddBlockShow(false);
      return;
    }
    setIsAddBlockShow(false);
    document.removeEventListener('click', onGlobalCloseClick);

    const updatedNicknames = [...product.nickName, ...newNicknames];
    onUpdateNickname(updatedNicknames, false, newNicknames.join(','));
  };

  const removeNickname = (nickname: string) => {
    nickname = nickname.trim();
    const newNicknames = [...product.nickName];
    const idx = newNicknames.findIndex((item) => item === nickname);
    newNicknames.splice(idx, 1);
    product.nickName = newNicknames;
    onUpdateNickname(newNicknames, true, nickname);
  };

  return (
    <div
      className={classnames(
        'tw-rounded tw-bg-white tw-my-8 tw-p-5 tw-flex',
        styles.itemWrapper,
      )}
    >
      <img
        src={
          product.squarishUrl ??
          'https://secure-images.nike.com/is/image/DotCom/848475_800'
        }
        className='rounded tw-w-20 tw-h-20 tw-object-fit'
        alt='product'
      />

      <div className='tw-ml-5 tw-flex-1'>
        {(() => {
          const inner = (
            <div>
              <div className='tw-mb-1'>
                <p className='tw-font-bold'>{product.copy?.title}</p>
                <p className='tw-text-sm'>{product.copy?.subtitle}</p>
              </div>
              <p className='tw-text-sm tw-text-blue-500'>
                {product.productCode || product._id}
                {product.productSubType === 'CUSTOMIZABLE' && (
                  <NBYIcon
                    className='tw-ml-2 tw-inline-block tw-align-middle'
                    width={16}
                    height={16}
                  />
                )}
                {product.productSubType === 'CUSTOMIZED' && (
                  <NBYIcon
                    className='tw-ml-2 tw-inline-block tw-align-middle'
                    width={16}
                    height={16}
                  />
                )}
                {product.productSubType !== 'STANDARD' && (
                  <span className='tw-ml-2 tw-text-gray-400'>
                    {product.productSubType}
                  </span>
                )}
              </p>
            </div>
          );
          return product.productSubType === 'CUSTOMIZABLE' ? (
            <Tooltip
              key={`${product._id}-customizable`}
              bodySlot={
                <p className='tw-text-sm'>
                  Nicknames under this product code will <br /> apply to all the
                  associated NBY products.
                </p>
              }
              placement={'right'}
              isDark
            >
              {inner}
            </Tooltip>
          ) : (
            inner
          );
        })()}
        {product.activations && product.activations.length > 0 && (
          <div className='tw-mt-2'>
            <button
              className='tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-gray-500 tw-cursor-pointer tw-bg-transparent tw-border-none tw-p-0 hover:tw-text-gray-800'
              onClick={() => setIsActivationsExpanded((v) => !v)}
            >
              <span
                className={classnames(
                  'tw-transition-transform tw-duration-200',
                  {
                    'tw-rotate-90': isActivationsExpanded,
                  },
                )}
              >
                ▶
              </span>
              <span>
                Channels (
                {new Set(product.activations.map((a) => a.channelId)).size})
              </span>
            </button>
            {isActivationsExpanded && (
              <div className='tw-mt-1 tw-flex tw-flex-col tw-gap-1'>
                {Object.values(
                  product.activations.reduce<
                    Record<
                      string,
                      {
                        channelId: string;
                        ranges: {
                          start?: string;
                          end?: string;
                          status: string;
                          statusModifier?: string;
                        }[];
                      }
                    >
                  >((acc, a) => {
                    const key = a.channelId;
                    if (!acc[key]) {
                      acc[key] = { channelId: a.channelId, ranges: [] };
                    }
                    acc[key].ranges.push({
                      start: a.start,
                      end: a.end,
                      status: a.status,
                      statusModifier: a.statusModifier,
                    });
                    return acc;
                  }, {}),
                ).map((a) => {
                  const ckey = `${product.productCode || product._id}_${
                    a.channelId
                  }`;
                  return (
                    <div
                      key={ckey}
                      className='tw-flex tw-items-start tw-gap-2 tw-text-xs'
                    >
                      <span className='tw-font-bold tw-w-20 tw-shrink-0 tw-rounded tw-px-2 tw-py-0.5 tw-border tw-border-solid tw-border-sky-900 tw-text-gray-600'>
                        {CHANNEL_NAMES[a.channelId] ?? a.channelId}
                      </span>
                      <div className='tw-flex tw-flex-col tw-gap-0.5'>
                        {a.ranges.map((r, i) => {
                          const status =
                            r.status === 'ACTIVE'
                              ? 'ACTIVE'
                              : r.statusModifier || r.status;
                          return (
                            <div
                              key={i}
                              className='tw-flex tw-items-center tw-gap-2'
                            >
                              <span
                                className={classnames(
                                  'tw-inline-block tw-font-bold tw-rounded tw-px-2 tw-py-0.5 tw-min-w-24 tw-shrink-0 tw-text-center',
                                  {
                                    'tw-text-emerald-200 tw-bg-emerald-500':
                                      status === 'ACTIVE',
                                    'tw-text-orange-200 tw-bg-orange-500':
                                      status === 'INACTIVE',
                                    'tw-text-yellow-200 tw-bg-yellow-500':
                                      status === 'HOLD',
                                    'tw-text-gray-200 tw-bg-gray-500': ![
                                      'ACTIVE',
                                      'INACTIVE',
                                      'HOLD',
                                    ].includes(status),
                                  },
                                )}
                              >
                                {status}
                              </span>
                              <span className='tw-text-gray-500'>
                                {r.start
                                  ? dayjs(r.start).format('YYYY-MM-DD HH:mm')
                                  : '—'}
                              </span>
                              <span className='tw-text-gray-400'>→</span>
                              <span className='tw-text-gray-500'>
                                {r.end
                                  ? dayjs(r.end).format('YYYY-MM-DD HH:mm')
                                  : '—'}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      <div className='tw-flex-1'>
        <div className='tw-flex tw-items-center tw-mb-2'>
          <Shoe size='s' />
          <p className='tw-text-sm'>Nick-names</p>

          {canWrite && (
            <div className='tw-relative'>
              <CreateRuleFilled
                className='tw-cursor-pointer tw-transition-all hover:tw-opacity-50 tw-ml-2'
                size='s'
                onClick={openAddBlock}
              />
              <div
                className={classnames(
                  'tw-absolute tw-z-10 tw-bg-white tw-shadow-2xl tw-px-5 tw-pt-6 tw-pb-6 tw-rounded tw-w-64',
                  styles.addNewBlock,
                  {
                    [styles.showUp]: isAddBlockShow,
                  },
                )}
                ref={addBlockRef}
              >
                <Input
                  placeholder='New Nick Name'
                  className='tw-border-b tw-w-34 tw-text-sm'
                  onSave={addNewNickname}
                  cleanAfterSave
                  ref={inputRef}
                />
                <p className='tw-text-xs tw-text-gray-400 tw-mt-1'>
                  Comma-separated for multiple, click enter to save.
                </p>
              </div>
            </div>
          )}
        </div>

        {Object.keys(convertResource).map((source: string, outIndex) => (
          <div key={outIndex} className='tw-flex tw-gap-2 '>
            <div
              key={`${outIndex}-${source}`}
              className='tw-text-right tw-flex-none tw-w-24 tw-gap-2 '
            >
              {source}:
            </div>
            <div
              key={`${outIndex}-inner`}
              className='tw-flex tw-flex-wrap tw-gap-2 tw-text-right tw-flex-row'
            >
              {convertResource[source].map((nickname, innerIndex) => {
                if (canWrite) {
                  return (
                    <Tooltip
                      key={`${nickname}-${innerIndex}`}
                      bodySlot={<p className='tw-text-sm'>click to remove</p>}
                      placement={'top'}
                      isDark
                    >
                      <span
                        className='tw-rounded-full tw-text-blue-600 tw-bg-blue-200 tw-text-sm tw-px-4 tw-py-0.5 tw-font-bold tw-transition-all hover:tw-bg-red-200  hover:tw-text-red-600 tw-cursor-pointer'
                        onClick={removeNickname.bind(null, nickname)}
                      >
                        {nickname}
                      </span>
                    </Tooltip>
                  );
                } else {
                  return (
                    <span
                      key={nickname}
                      className='tw-rounded-full tw-text-blue-600 tw-bg-blue-200 tw-text-sm tw-px-4 tw-py-0.5 tw-font-bold tw-transition-all hover:tw-bg-red-200  hover:tw-text-red-600 tw-cursor-pointer'
                    >
                      {nickname}
                    </span>
                  );
                }
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
